/*
 * @Descripttion: 场景管理 API（合并场景分类 + 场景价格管理）
 * @Date: 2024-08-06 10:12:42
 */
import http from '@/utils/http/axios';
import {
  sceneManageDetailToYuan,
  scenePageToYuan,
  buildSceneManageSaveParams
} from './translators';
import type {
  GetEnterprisePageList,
  PostIntelligentAssist,
  PostScenePlanSave,
  GetDrugList,
  SceneManagePage,
  SceneManageDetail,
  SceneManageSave,
  SceneProductList,
  SceneSceneList
} from './types';

const yApiObj = {
  // yapi: 1271
};

const requestConfig = {
  isParseToJson: true,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  },
  ...yApiObj
};

enum Api {
  // 场景价格管理
  enterprisePageList = '/entp/user/api/v1/pageList',
  // 服务商场景策划
  intelligentAssist = '/scenePlan/api/v1/intelligentAssist',
  scenePlanSave = '/scenePlan/api/v1/save',
  // 药品列表（选场弹窗）
  getDrugList = '/project/pair/productList',
  // ===== SceneManage 新接口（接口文档 V2；IAS REQUEST_PATH='' 需手动加 /front-end 前缀）=====
  sceneManagePage = '/front-end/sceneManage/api/v1/config/page',
  sceneManageDetail = '/front-end/sceneManage/api/v2/config/detail',
  sceneManageSave = '/front-end/sceneManage/api/v2/config/save',
  // V2 新增查询接口
  sceneProductList = '/front-end/sceneManage/api/v2/product/page',
  sceneSceneList = '/front-end/sceneManage/api/v2/product/scene/page'
}

// ======================== 场景价格管理 ========================

/**
 * 企业分页列表（工业下拉搜索）
 */
export const enterprisePageList: GetEnterprisePageList = params =>
  http.request(
    {
      url: Api.enterprisePageList,
      method: 'POST',
      params
    },
    requestConfig
  );

/**
 * 获取药品列表（选场弹窗前置药品选择）
 * TODO 缺少接口：当前复用 project-mgmt-initiate 的 productList，返回结构需后端确认
 * 来源：/project/pair/productList（project-mgmt-initiate/src/data-source/apis.ts L32）
 * 若后端确认新接口，替换 Api.getDrugList 即可
 */
export const getDrugList: GetDrugList = async params => {
  const resp = await http.request(
    {
      url: Api.getDrugList,
      method: 'POST',
      params: {
        ...params
      }
    },
    requestConfig
  );
  // 适配翻译：接口返回字段名可能是 productName，映射为 drugName
  const rawList = resp?.data?.list || resp?.data || [];
  resp.data = {
    list: (Array.isArray(rawList) ? rawList : []).map((item: any) => ({
      id: item.id ?? item.productId,
      drugName: item.productName || item.drugName || item.name || '',
      productCategory: item.productCategory || item.medicineType || '',
      productAttribute: item.productAttribute || item.jixing || '',
      lifecycleStage: item.lifecycleStage || ''
    })),
    total: rawList.length || 0
  };
  return resp;
};

// ======================== 服务商场景策划（真实接口） ========================

/**
 * 接口一：智能辅助筛选场景
 */
export const postIntelligentAssist: PostIntelligentAssist = params =>
  http.request(
    {
      url: Api.intelligentAssist,
      method: 'POST',
      params
    },
    requestConfig
  );

/**
 * 接口二：保存场景策划
 */
export const postScenePlanSave: PostScenePlanSave = params =>
  http.request(
    {
      url: Api.scenePlanSave,
      method: 'POST',
      params
    },
    requestConfig
  );

/******* SceneManage 新接口（接口文档 V2，统一 /sceneManage/api/v1）*************************/

/**
 * 接口 A：配置分页列表
 * 三端统一列表（planScope=1 工业价格 / =2 服务商场景），后端按登录身份强制补充 platformId/industryId/aspId
 */
export const sceneManagePage: SceneManagePage = params =>
  http.request(
    {
      url: Api.sceneManagePage,
      method: 'POST',
      params
    },
    requestConfig
  );

/**
 * 接口 B：配置详情（合并产品及场景选择数据）
 * 返回 SceneManageDetailVO：products[].scenes[] 双层结构 + selected 回显 + 本地筛选 options
 */
export const sceneManageDetail: SceneManageDetail = async params => {
  const resp = await http.request(
    {
      url: Api.sceneManageDetail,
      method: 'POST',
      params
    },
    requestConfig
  );
  // sceneTreeList 前端不使用且数据量大：接收后剔除，减少内存占用（网络层传输需后端配合去掉）
  const products = resp?.data?.products;
  if (Array.isArray(products)) {
    products.forEach((product: any) => {
      delete product.sceneTreeList;
    });
  }
  // 金额分→元（translators 层转换，apis 层调用）
  return sceneManageDetailToYuan(resp);
};

/**
 * V2 接口2：企业产品分页（弹窗左侧）
 * 出参 SceneManageV2ProductPageDTO（page 分页，前端固定 pageSize=100 + productName 远程搜索）
 */
export const getSceneProductList: SceneProductList = params =>
  http.request(
    {
      url: Api.sceneProductList,
      method: 'POST',
      params
    },
    requestConfig
  );

/**
 * V2 接口3：单产品场景分页（弹窗右侧，按产品查询）
 * 出参 SceneManageV2ProductScenePageDTO（scenePage 分页 + 4 options；planId 有值时叠加已配置）
 */
export const getSceneSceneList: SceneSceneList = async params => {
  const resp = await http.request(
    {
      url: Api.sceneSceneList,
      method: 'POST',
      params
    },
    requestConfig
  );
  // 金额分→元（translators 层转换，apis 层调用）
  return scenePageToYuan(resp);
};

/**
 * V2 接口5：配置整包保存（新增/编辑/删除/价格/预算统一保存）
 * 页面传元数据 → buildSceneManageSaveParams 构造精简提交体 + 元→分（translators 层转换，apis 层调用）
 */
export const sceneManageSave: SceneManageSave = params =>
  http.request(
    {
      url: Api.sceneManageSave,
      method: 'POST',
      params: buildSceneManageSaveParams(params)
    },
    requestConfig
  );
